// loginWithOAuth.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import handleFetchError from '../resource/handleFetchError.js';

import getMeData from '../selector/getMeData.js';
import loadUrlOAuthLogin from './loadUrlOAuthLogin.js';
import fetchBindOAuth from './fetchBindOAuth.js';
import pushToastr from '../action/pushToastr.js';
import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  SET_OPERATION_DATA,
  MERGE_OPERATION_DATA,
  LOGOUT,
} from '../ActionTypes.js';
import { AuthId } from '../resource/loginRegisterConstants.js';
import oAuthConfigs from '../resource/oAuthConfigs.js';
import fetchAccessToken, { viaTypes } from '../action/fetchAccessToken.js';
import toggleModal from '../action/toggleModal.js';
import { register as registerModal } from '../resource/modalId.js';

let windowObject = null;
let previousHandler = null;

/**
 * Login with oAuths
 * @kind action
 * @param {string} {oAuth} - oAuth type. ['facebook', 'twitter', 'google-oauth2']
 * @param {bool} {isBinding} - is this for account linking.
 * @param {string} {entry} - start login entry. ['login', 'register']
 * @param {object} {appleMigration} - apple migration data { partialToken, pathname }.
 * @return {Promise} Action promise.
 */
const loginWithOAuth =
  ({ oAuth, isBinding = false, entry = 'register', appleMigration }) =>
  async (dispatch, getState) => {
    const oAuthData = oAuthConfigs[oAuth];
    if (!oAuthData) {
      return dispatch({ type: '' });
    }

    if (previousHandler) {
      window.removeEventListener('message', previousHandler);
      previousHandler = null;
    }
    if (windowObject && !windowObject.closed) {
      windowObject.close();
      windowObject = null;
    }

    dispatch({
      type: SET_OPERATION_DATA,
      payload: { selectPath: ['login', 'entry'], data: entry },
    });

    const selectPath = ['login', oAuth, 'verify'];

    const url = getResourceUrl({ endpoint: oAuthData.url });
    const redirectUrl = new URL(
      '/static/html/oAuthLogin.html',
      location.origin
    );
    if (!isBinding) {
      redirectUrl.search = location.search;
    }
    const utm = getMeData(getState(), 'utm');
    if (utm) {
      Object.keys(utm)
        .filter(key => utm[key])
        .forEach(key => redirectUrl.searchParams.set(key, utm[key]));
    }

    redirectUrl.searchParams.set(
      'action',
      `${isBinding ? 'bind' : 'login'}-${oAuthData.id}`
    );
    redirectUrl.searchParams.set('entry', entry);
    redirectUrl.searchParams.set('pathname', location.pathname);
    url.searchParams.set('next', redirectUrl.href);

    const fetchOptions = {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...getHeaders(),
      },
    };

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      const response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }

      dispatch({ type: SET_NETWORKING_SUCCESS, payload: { selectPath } });
      const resultUrl =
        response.headers.get('Location') || response.headers.get('location');

      if (!getIsOnMobile()) {
        // eslint-disable-next-line require-atomic-updates
        windowObject = window.open?.(
          resultUrl,
          oAuthData.window.title,
          oAuthData.window.argument
        );
      }

      // will be null if user blocks popup window
      if (windowObject) {
        const handler = async ({ data: { href } }) => {
          // Might be un-related messages.
          if (!href) {
            return;
          }
          window.removeEventListener('message', handler);

          try {
            const url = new URL(href);
            windowObject && windowObject.close();
            if (appleMigration) {
              // id_token is required for migration binding; fallback to normal login if missing.
              const idToken = url.searchParams.get('id_token');
              if (!idToken) {
                return dispatch(loadUrlOAuthLogin({ url }));
              }
              const APPLE_LOGIN_ENDPOINT = `/login/${AuthId.APPLE_ID_NEW}`;
              const MODAL_ID = registerModal.APPLE_MIGRATION_ALERT;
              const linkUrl = getResourceUrl({
                endpoint: APPLE_LOGIN_ENDPOINT,
              });
              linkUrl.searchParams.set(
                'partial_token',
                appleMigration.partialToken
              );
              linkUrl.searchParams.set('old_id_token', idToken);
              try {
                const linkResponse = await fetch(linkUrl.href, {
                  method: 'POST',
                  headers: { ...getHeaders() },
                  credentials: 'same-origin',
                });
                if (!linkResponse.ok) {
                  await handleFetchError({ response: linkResponse });
                }
                dispatch({ type: LOGOUT });
                const { refresh_token: linkRefreshToken } =
                  await linkResponse.json();
                if (!linkRefreshToken) {
                  throw new Error('token_not_found');
                }
                await dispatch(
                  fetchAccessToken({
                    refreshToken: linkRefreshToken,
                    via: viaTypes.JWT_LOGIN,
                  })
                );
                const token = getMeData(getState(), 'token');
                if (!token) {
                  throw new Error('token_not_found');
                }
                history.replaceState(null, '', appleMigration.pathname);
                dispatch({
                  type: MERGE_OPERATION_DATA,
                  payload: {
                    selectPath: ['login'],
                    data: { entry },
                  },
                });
                return dispatch(
                  toggleModal({
                    modalId: MODAL_ID,
                    isOpen: true,
                    payload: { variant: 'success', token, entry },
                    modalOption: { isHigherThanAll: true },
                  })
                );
              } catch (migrationError) {
                let errorStatus;
                try {
                  errorStatus = JSON.parse(migrationError.message).status;
                } catch (_) {
                  // non-JSON error message
                }
                const variant = errorStatus === 400 ? 'errorFinal' : 'error';
                return dispatch(
                  toggleModal({
                    modalId: MODAL_ID,
                    isOpen: true,
                    payload: {
                      partialToken: appleMigration.partialToken,
                      entry,
                      pathname: appleMigration.pathname,
                      variant,
                    },
                    modalOption: { isHigherThanAll: true },
                  })
                );
              }
            }
            if (isBinding) {
              return dispatch(fetchBindOAuth({ oAuth, url }));
            } else {
              return dispatch(loadUrlOAuthLogin({ url }));
            }
          } catch (_) {
            return;
          }
        };
        previousHandler = handler;
        window.addEventListener('message', handler);
      } else {
        location.assign(resultUrl);
      }

      return { type: '' };
    } catch (error) {
      let textKey = 'retry_failed_reason';
      try {
        const { code } = JSON.parse(error.message);
        if (code) {
          textKey = getTranslationKey({ key: `error_${code}` });
        }
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (textKey) {
        dispatch(pushToastr({ textKey, color: 'error' }));
      }

      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default loginWithOAuth;
